import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import StarIcon from "@mui/icons-material/Star";
import StarBorderIcon from "@mui/icons-material/StarBorder";
import ReplayIcon from "@mui/icons-material/Replay";
import StarOutlineIcon from "@mui/icons-material/StarOutline";
import React, { useContext, useState } from "react";
import { useNavigate } from "react-router";
import { Link as RouterLink } from "react-router-dom";
import { useMutation } from "@apollo/client";
import gql from "graphql-tag";
import ConfigurationContext from "../../../context/Configuration";
import UserContext from "../../../context/User";
import { reviewOrder } from "../../../apollo/server";
import { resizedImage } from "../../../utils/resizedImage";

const REVIEW_ORDER = gql`${reviewOrder}`;

const STATUS_STYLE = {
  DELIVERED: { label: "Delivered",  color: "#10B981", bg: "#ECFDF5" },
  PICKED:    { label: "Picked Up",  color: "#10B981", bg: "#ECFDF5" },
  CANCELLED: { label: "Cancelled",  color: "#EF4444", bg: "#FEF2F2" },
  COMPLETED: { label: "Completed",  color: "#10B981", bg: "#ECFDF5" },
};

function StarRow({ value, onChange }) {
  const [hovered, setHovered] = useState(0);
  return (
    <Box sx={{ display: "flex", gap: 0.5, justifyContent: "center" }}>
      {[1, 2, 3, 4, 5].map((star) => (
        <Box
          key={star}
          onMouseEnter={() => setHovered(star)}
          onMouseLeave={() => setHovered(0)}
          onClick={() => onChange(star)}
          sx={{ cursor: "pointer", fontSize: 0, lineHeight: 0 }}
        >
          {(hovered || value) >= star ? (
            <StarIcon sx={{ fontSize: 40, color: "#F59E0B" }} />
          ) : (
            <StarBorderIcon sx={{ fontSize: 40, color: "#D1D5DB" }} />
          )}
        </Box>
      ))}
    </Box>
  );
}

function ReviewDialog({ open, onClose, orderId, restaurantName, onDone }) {
  const theme = useTheme();
  const [rating, setRating] = useState(0);
  const [description, setDescription] = useState("");

  const [mutate, { loading }] = useMutation(REVIEW_ORDER, {
    onCompleted: () => {
      onDone();
      onClose();
    },
    onError: (err) => {
      console.error("Review error:", err.message);
    },
  });

  const canSubmit = rating >= 1;

  const handleSubmit = () => {
    if (!canSubmit) return;
    mutate({ variables: { order: orderId, rating, description: description.trim() } });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{ sx: { borderRadius: 3, p: 0.5 } }}
    >
      <DialogTitle sx={{ pr: 6, pb: 0.5 }}>
        <Typography fontWeight={700} fontSize="1.05rem">Rate your order</Typography>
        {restaurantName && (
          <Typography variant="caption" sx={{ color: "#6B7280" }}>{restaurantName}</Typography>
        )}
        <IconButton
          onClick={onClose}
          size="small"
          sx={{ position: "absolute", top: 10, right: 10, color: "#9CA3AF" }}
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ pt: 2 }}>
        {/* Stars */}
        <Box sx={{ mb: 2 }}>
          <StarRow value={rating} onChange={setRating} />
          <Typography
            variant="caption"
            sx={{ display: "block", textAlign: "center", mt: 0.75, color: "#9CA3AF", minHeight: 18 }}
          >
            {["", "Poor", "Fair", "Good", "Very Good", "Excellent"][rating] || ""}
          </Typography>
        </Box>

        {/* Text input */}
        <TextField
          multiline
          rows={3}
          fullWidth
          variant="outlined"
          placeholder="Optional: What did you think? (More detail helps other customers)"
          value={description}
          onChange={(e) => setDescription(e.target.value)}
          sx={{
            "& .MuiOutlinedInput-root": {
              borderRadius: 2,
              fontSize: "0.875rem",
            },
          }}
        />

        {/* Submit */}
        <Button
          fullWidth
          variant="contained"
          disableElevation
          disabled={!canSubmit || loading}
          onClick={handleSubmit}
          sx={{
            mt: 2,
            borderRadius: 2,
            py: 1.2,
            fontWeight: 700,
            fontSize: "0.875rem",
            textTransform: "none",
            bgcolor: theme.palette.primary.main,
            "&:hover": { bgcolor: theme.palette.primary.dark },
          }}
        >
          {loading ? <CircularProgress size={18} color="inherit" /> : "Submit Review"}
        </Button>

      </DialogContent>
    </Dialog>
  );
}

function PastOrderCard({ item }) {
  const theme = useTheme();
  const navigate = useNavigate();
  const small = useMediaQuery(theme.breakpoints.down("sm"));
  const [loading, setLoading] = useState(false);
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const configuration = useContext(ConfigurationContext);
  const { setCartRestaurant, addCartItem } = useContext(UserContext);

  const rawStyle = STATUS_STYLE[item.orderStatus] || { label: item.orderStatus, color: "#6B7280", bg: "#F9FAFB" };
  const style =
    item.supportTicketStatus === "RESOLVED"
      ? { label: "Refunded", color: "#7c3aed", bg: "#f5f3ff" }
      : item.supportTicketStatus === "CLOSED_NO_ACTION"
      ? { label: "Disputed", color: "#b45309", bg: "#fffbeb" }
      : item.orderStatus === "DELIVERED" && item.isPickedUp
      ? { ...rawStyle, label: "Picked Up" }
      : rawStyle;

  const grandTotal =
    parseFloat(item.orderAmount || 0) +
    (item.isPickedUp ? 0 : parseFloat(item.deliveryCharges || 0)) +
    parseFloat(item.tipping || 0) +
    parseFloat(item.taxationAmount || 0) +
    parseFloat(item.serviceFee || 0);

  const sym = configuration.currencySymbol;

  // Show review button for any completed/delivered order that hasn't been reviewed yet
  const canReview = (item.orderStatus === "DELIVERED" || (item.orderStatus === "PICKED" && item.isPickedUp)) && !item.review && !reviewed;

  const onAddToCart = async () => {
    setLoading(true);
    try {
      await setCartRestaurant(item.restaurant._id);
      item.items.forEach(async (cartItem, index) => {
        const addons = cartItem.addons.map((addon) => ({
          _id: addon._id,
          options: addon.options.map(({ _id }) => ({ _id })),
        }));
        await addCartItem(
          cartItem.food,
          cartItem.variation._id,
          cartItem.quantity,
          addons,
          index === 0
        );
      });
      navigate(`/restaurant/${item.restaurant.slug}`, {
        state: {
          id: item.restaurant._id,
          name: item.restaurant.name,
          image: item.restaurant.image,
          slug: item.restaurant.slug,
        },
      });
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <RouterLink
        to={`/order-detail/${item._id}`}
        style={{ textDecoration: "none", display: "block", marginBottom: 16 }}
      >
        <Paper
          elevation={0}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: "1px solid #F3F4F6",
            borderLeft: `4px solid ${style.color}`,
            boxShadow: "0 2px 8px rgba(0,0,0,0.06)",
            bgcolor: "white",
            transition: "transform 0.15s ease, box-shadow 0.15s ease",
            "&:hover": {
              transform: "translateY(-2px)",
              boxShadow: "0 6px 20px rgba(0,0,0,0.10)",
            },
          }}
        >
          {/* Card body */}
          <Box sx={{ p: 2.5, display: "flex", gap: 2, alignItems: "flex-start" }}>
            {/* Restaurant image */}
            <Box
              component="img"
              src={resizedImage(item.restaurant?.logo || item.restaurant?.image, 240)}
              alt="restaurant"
              sx={{
                width: small ? 56 : 80,
                height: small ? 56 : 80,
                borderRadius: "12px",
                objectFit: "contain",
                border: "2px solid #F3F4F6",
                flexShrink: 0,
                backgroundColor: "#f9f9f9",
              }}
            />

            {/* Content */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              {/* Row 1: name + status chip */}
              <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.4}>
                <Typography
                  variant="subtitle1"
                  fontWeight={700}
                  sx={{
                    color: "#111827",
                    lineHeight: 1.3,
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    maxWidth: small ? 140 : 260,
                  }}
                >
                  {item.restaurant?.name ?? "..."}
                </Typography>
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.4,
                    bgcolor: style.bg,
                    color: style.color,
                    borderRadius: 20,
                    border: `1px solid ${style.color}`,
                    fontSize: "0.68rem",
                    fontWeight: 700,
                    ml: 1,
                    flexShrink: 0,
                  }}
                >
                  {style.label}
                </Box>
              </Box>

              {/* Row 2: order no + type + date */}
              <Box display="flex" alignItems="center" gap={0.75} flexWrap="wrap" sx={{ mb: 0.75 }}>
                <Typography variant="caption" sx={{ color: "#6B7280", fontWeight: 600 }}>
                  #{item.orderId ?? "—"}
                </Typography>
                <Typography variant="caption" sx={{ color: "#D1D5DB" }}>·</Typography>
                <Box
                  sx={{
                    px: 0.75,
                    py: 0.15,
                    borderRadius: 10,
                    fontSize: "0.62rem",
                    fontWeight: 600,
                    bgcolor: item.isPickedUp ? "#F5F3FF" : "#EFF6FF",
                    color: item.isPickedUp ? "#7C3AED" : "#2563EB",
                    border: `1px solid ${item.isPickedUp ? "#DDD6FE" : "#BFDBFE"}`,
                  }}
                >
                  {(() => {
                    if (item.isPickedUp) return 'Pickup';
                    if (item.rider?.driverType === 'RESTAURANT') return 'Delivered by restaurant';
                    if (item.rider?.driverType === 'RUSHMYFOOD') return 'Delivered by RushMyFood';
                    if (item.firstDeliveryOrderId) return 'Delivered by RushMyFood';
                    if (item.isSelfDelivery) return 'Delivered by restaurant';
                    return 'Delivered by RushMyFood';
                  })()}
                </Box>
                <Typography variant="caption" sx={{ color: "#D1D5DB" }}>·</Typography>
                <Typography variant="caption" sx={{ color: "#9CA3AF" }}>
                  {new Date(item.createdAt).toLocaleString("en-US", {
                    weekday: "short", month: "short", day: "numeric",
                    year: "numeric", hour: "2-digit", minute: "2-digit",
                  })}
                </Typography>
              </Box>

              {/* Row 3: items list */}
              {item.items?.slice(0, 2).map((oi, i) => (
                <Typography key={oi._id ?? i} variant="caption" sx={{ color: "#374151", display: "block", fontSize: "0.76rem", mb: 0.2 }}>
                  <span style={{ color: "#9CA3AF", marginRight: 3 }}>{oi.quantity}×</span>
                  {oi.title}
                  {oi.variation?.title ? <span style={{ color: "#9CA3AF" }}> ({oi.variation.title})</span> : null}
                </Typography>
              ))}
              {(item.items?.length ?? 0) > 2 && (
                <Typography variant="caption" sx={{ color: "#9CA3AF", fontSize: "0.7rem" }}>
                  +{item.items.length - 2} more
                </Typography>
              )}

              {/* Row 4: grand total */}
              <Typography variant="caption" sx={{ color: "#111827", fontWeight: 700, display: "block", mt: 0.75, fontSize: "0.8rem" }}>
                Total: {sym} {grandTotal.toFixed(2)}
              </Typography>
            </Box>
          </Box>

          {/* Action footer */}
          <Box
            sx={{
              px: 2.5,
              py: 1.5,
              bgcolor: "#F9FAFB",
              borderTop: "1px solid #F3F4F6",
              display: "flex",
              gap: 1.5,
              alignItems: "center",
            }}
            onClick={(e) => e.preventDefault()}
          >
            <Button
              size="small"
              variant="contained"
              disabled={loading}
              startIcon={loading ? null : <ReplayIcon sx={{ fontSize: "14px !important" }} />}
              onClick={onAddToCart}
              disableElevation
              sx={{
                bgcolor: theme.palette.primary.main,
                "&:hover": { bgcolor: theme.palette.primary.dark },
                borderRadius: 20,
                px: 2,
                py: 0.6,
                fontSize: "0.75rem",
                fontWeight: 600,
                textTransform: "none",
              }}
            >
              {loading ? <CircularProgress size={14} color="inherit" /> : "Reorder"}
            </Button>

            {canReview && (
              <Button
                size="small"
                variant="outlined"
                startIcon={<StarOutlineIcon sx={{ fontSize: "14px !important" }} />}
                onClick={(e) => { e.preventDefault(); setReviewOpen(true); }}
                sx={{
                  borderColor: "#F59E0B",
                  color: "#92400E",
                  bgcolor: "#FFFBEB",
                  "&:hover": { borderColor: "#D97706", bgcolor: "#FEF3C7" },
                  borderRadius: 20,
                  px: 2,
                  py: 0.6,
                  fontSize: "0.75rem",
                  fontWeight: 600,
                  textTransform: "none",
                }}
              >
                Leave a Review
              </Button>
            )}

            {(item.review || reviewed) && (
              <Typography variant="caption" sx={{ color: "#10B981", fontWeight: 600, display: "flex", alignItems: "center", gap: 0.5 }}>
                <StarIcon sx={{ fontSize: 14, color: "#10B981" }} /> Reviewed
              </Typography>
            )}
          </Box>
        </Paper>
      </RouterLink>

      <ReviewDialog
        open={reviewOpen}
        onClose={() => setReviewOpen(false)}
        orderId={item._id}
        restaurantName={item.restaurant?.name}
        onDone={() => setReviewed(true)}
      />
    </>
  );
}

export default React.memo(PastOrderCard);
